import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, ViewChild, viewChild } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, ValidatorFn, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { NgSelectModule } from '@ng-select/ng-select';
import { BehaviorSubject } from 'rxjs';

import { SitesQuery } from '@models/sites/sites.query';
import { SitesService } from '@models/sites/sites.service';

import { CustomValidators } from '@shared/components/base-form-control/custom-validators';
import { FileManagerComponent } from '@shared/components/file-manager/file-manager.component';
import { FileViewerComponent } from '@shared/components/file-viewer/file-viewer.component';
import { InputComponent } from '@shared/components/input/input.component';
import { ModalComponent } from '@shared/components/modals/modal/modal.component';
import { CustomOverlayRef } from '@shared/components/overlay/custom-overlay-ref';
import { ROUTING_PATH } from '@shared/constants/routingPath';
import { FormErrorDirective } from '@shared/directives/form-error.directive';
import { FileMetadata } from '@shared/services/api.service';
import { DocumentLibraryService } from '@shared/services/document-library.service';
import { Currency, DocumentType, EntityType, EventType } from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { AuthQuery } from '@shared/store/auth/auth.query';
import { EventService } from '@shared/store/event/event.service';
import { MainQuery } from '@shared/store/main/main.query';
import { OrganizationQuery } from '@shared/store/organization/organization.query';
import { Utils } from '@shared/utils/utils';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    InputComponent,
    NgSelectModule,
    FormErrorDirective,
    AsyncPipe,
    FileManagerComponent,
    FileViewerComponent,
    ModalComponent,
  ],
  selector: 'aux-add-custom-site-dialog',
  styles: [
    `
      :host {
        display: block;
      }
    `,
  ],
  templateUrl: './add-custom-site-dialog.component.html',
})
export class AddCustomSiteDialogComponent {
  public organizationQuery = inject(OrganizationQuery);
  public ref = inject(CustomOverlayRef<undefined>);

  private authQuery = inject(AuthQuery);
  private documentLibraryService = inject(DocumentLibraryService);
  private eventService = inject(EventService);
  private formBuilder = inject(UntypedFormBuilder);
  private mainQuery = inject(MainQuery);
  private overlayService = inject(OverlayService);
  private router = inject(Router);
  private sitesQuery = inject(SitesQuery);
  private sitesService = inject(SitesService);

  readonly closeoutInput = viewChild<InputComponent>('closeoutInput');

  countries: { label: string; value: string }[];
  currencyOptions = Object.entries(Utils.CURRENCY_OPTIONS).map(([key, label]) => ({
    key,
    label,
  }));
  fg = this.formBuilder.group({
    address_line_1: '',
    address_line_2: '',
    address_line_3: '',
    city: '',
    closeout_date: null,
    country: ['', Validators.required],
    currency: ['', Validators.required],
    family_name: '',
    given_name: '',
    managed_by_id: ['', Validators.required],
    name: ['', Validators.required],
    site_activation: null,
    site_no: '',
    state: '',
    target_patients: 0,
    zip: '',
  });
  @ViewChild(FileManagerComponent) fileManager: FileManagerComponent | undefined;
  loading$ = new BehaviorSubject(false);

  constructor() {
    this.countries = Utils.getCountriesForSelectOptions();
  }

  getMetadata(): FileMetadata {
    return { onboarding: 'true' };
  }

  // eslint-disable-next-line max-lines-per-function
  async onAddTrialSite() {
    if (this.fg.valid && !this.loading$.getValue() && this.closeoutInput()?.fc.valid) {
      const {
        address_line_1,
        address_line_2,
        address_line_3,
        city,
        closeout_date,
        country,
        currency,
        family_name,
        given_name,
        managed_by_id,
        name,
        site_activation,
        site_no,
        state,
        target_patients,
        zip,
      } = this.fg.value;

      this.loading$.next(true);
      const id = await this.sitesService.add({
        address_line_1,
        address_line_2,
        address_line_3,
        city,
        closeout_date,
        country,
        currency: Utils.CURRENCY_OPTIONS[+currency] as Currency,
        investigator: {
          family_name,
          given_name,
        },
        managed_by_id,
        name,
        site_activation,
        site_no,
        state,
        target_patients,
        zip,
      });

      if (id) {
        const trialId = this.mainQuery.getValue().trialKey;
        const path = `trials/${trialId}/sites/${id}/contracts/`;

        if (this.fileManager) {
          const files = this.fileManager.fileQuery.getAll();

          // eslint-disable-next-line max-depth
          for (const file of files) {
            const bucket_key = `${path}${file.bucket_key}`;

            this.fileManager.fileStore.update(file.id, { ...file, bucket_key });
            this.documentLibraryService
              .uploadDocument(
                { ...file, bucket_key },
                {
                  documentType: DocumentType.DOCUMENT_SITE_AGREEMENTS,
                  entity_id: id,
                  entity_type_id: EntityType.SITE,
                  site: id,
                },
                bucket_key
              )
              .catch(console.error);
          }

          // eslint-disable-next-line max-depth
          if (files.length > 0) {
            this.eventService.processEvent$({
              entity_id: '',
              entity_type: EntityType.DOCUMENT,
              payload: JSON.stringify({
                file_names: files.map((file) => file.fileName).join(', '),
                site_id: id,
                uploaded_by: `${this.authQuery.getFullName()} ${this.authQuery.getEmail()}`,
              }),
              type: EventType.SITE_CONTRACT_UPLOADED,
            });
          }

          const success = await this.fileManager.fileService.uploadFiles({
            metadata: this.getMetadata(),
          });

          // eslint-disable-next-line max-depth
          if (success) {
            this.overlayService.success('Trial site successfully saved!');
          }
        }
      }

      this.ref.close();
      this.loading$.next(false);
      void this.router.navigate([
        `${ROUTING_PATH.INVESTIGATOR.INDEX}/${ROUTING_PATH.INVESTIGATOR.SITE_COSTS.INDEX}/${ROUTING_PATH.INVESTIGATOR.SITE_COSTS.SITES.INDEX}/${id}/${ROUTING_PATH.INVESTIGATOR.SITE_COSTS.SITES.DETAILS}`,
      ]);
    }
  }

  onBlurSiteActivationPicker(closeoutDate: InputComponent) {
    // mark as touched and trigger validation
    closeoutDate.fc.markAsTouched();
    closeoutDate.fc.markAsDirty();
    closeoutDate.onBlur();
  }

  get siteNoValidators(): ValidatorFn[] {
    if (this.loading$.getValue()) {
      return [Validators.required];
    }

    const existingSiteNumbers = this.sitesQuery.sites().map((data) => data.site_no);

    return [
      Validators.required,
      CustomValidators.valueNotInList(existingSiteNumbers, 'duplicatedSite'),
    ];
  }
}
